/**
 * Agency portal shared UI atoms (SYR-85).
 *
 * Small presentational pieces used across Portfolio / Compare / Scorecard.
 * Chrome tokens come from the marketer-view THEME; score semantics follow the
 * two-palette rule (element ring colour = which, band palette = how good).
 */

import React from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';
import { BAND_PALETTE } from '../../components/MarketerView/config/bandColours';
import type { IAgencyBrandBucket, IAgencySignal } from '../../services/nestjs-base-api';

/** Flat amber primary button — The Briefing's `primaryBtn` contract. */
export const primaryBtnStyle: React.CSSProperties = {
    border: 'none',
    background: THEME.primary,
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    padding: '10px 20px',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
};

export const secondaryBtnStyle: React.CSSProperties = {
    border: `1px solid ${THEME.border}`,
    background: '#fff',
    color: THEME.textBase,
    fontSize: 14,
    fontWeight: 600,
    padding: '9px 18px',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
};

/** Uppercase section eyebrow. */
export const AgencyEyebrow: React.FunctionComponent<{ children: React.ReactNode }> = ({
    children,
}) => (
    <div
        style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: THEME.textMuted,
            marginBottom: 10,
        }}
    >
        {children}
    </div>
);

/** Page H1 + subtitle. */
export const AgencyPageHeader: React.FunctionComponent<{
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    extra?: React.ReactNode;
}> = ({ title, subtitle, extra }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 24,
            flexWrap: 'wrap',
        }}
    >
        <div style={{ minWidth: 0 }}>
            <h1
                style={{
                    fontSize: 30,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    margin: 0,
                    lineHeight: 1.12,
                    color: THEME.textBase,
                }}
            >
                {title}
            </h1>
            {subtitle && (
                <p
                    style={{
                        color: THEME.textSecondary,
                        fontSize: 15.5,
                        lineHeight: 1.6,
                        margin: '6px 0 0',
                        maxWidth: 640,
                    }}
                >
                    {subtitle}
                </p>
            )}
        </div>
        {extra && <div style={{ flexShrink: 0 }}>{extra}</div>}
    </div>
);

/** Business-type × product-type bucket badges. */
export const BucketBadges: React.FunctionComponent<{ bucket: IAgencyBrandBucket }> = ({
    bucket,
}) => {
    const chip: React.CSSProperties = {
        fontSize: 11.5,
        fontWeight: 600,
        color: THEME.textSecondary,
        background: THEME.bgChip,
        border: `1px solid ${THEME.border}`,
        padding: '2px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
    };
    if (!bucket.businessTypeName && !bucket.productTypeName) return null;
    // Verticals like Sports Betting use the same name for both dimensions —
    // show one badge instead of a duplicate pair.
    const duplicate = bucket.businessTypeName === bucket.productTypeName;
    return (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {bucket.businessTypeName && <span style={chip}>{bucket.businessTypeName}</span>}
            {bucket.productTypeName && !duplicate && (
                <span style={chip}>{bucket.productTypeName}</span>
            )}
        </span>
    );
};

/** Signed delta chip vs the previous period: "+3.7" green / "-12.4" orange. */
export const DeltaChip: React.FunctionComponent<{ delta: number | null }> = ({ delta }) => {
    const intl = useIntl();
    if (delta === null) return null;
    const up = delta >= 0;
    const colour = up ? '#059669' : '#dc6803';
    return (
        <span
            title={intl.formatMessage({
                id: 'agency.delta.tooltip',
                defaultMessage: 'Change vs the previous period',
            })}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                fontSize: 13,
                fontWeight: 700,
                color: colour,
                whiteSpace: 'nowrap',
            }}
        >
            {up ? (
                <ArrowUpOutlined style={{ fontSize: 11 }} />
            ) : (
                <ArrowDownOutlined style={{ fontSize: 11 }} />
            )}
            {`${up ? '+' : ''}${delta.toFixed(1)}`}
        </span>
    );
};

const SIGNAL_META: Record<
    IAgencySignal['type'],
    { band: keyof typeof BAND_PALETTE; id: string; def: string }
> = {
    score_drop: { band: 'weak', id: 'agency.signal.scoreDrop', def: 'Score dropped {value} pts' },
    gone_quiet: { band: 'watch', id: 'agency.signal.goneQuiet', def: 'Quiet for {value} days' },
    below_benchmark: { band: 'watch', id: 'agency.signal.belowBenchmark', def: 'Below benchmark' },
    insufficient_data: { band: 'na', id: 'agency.signal.insufficientData', def: 'Collecting data' },
};

/** One needs-attention signal chip. */
export const SignalChip: React.FunctionComponent<{ signal: IAgencySignal }> = ({ signal }) => {
    const meta = SIGNAL_META[signal.type];
    const palette = BAND_PALETTE[meta.band];
    const value =
        signal.type === 'score_drop'
            ? Math.abs(signal.value ?? 0).toFixed(1)
            : String(signal.value ?? '');
    return (
        <span
            style={{
                fontSize: 11,
                fontWeight: 700,
                color: palette.fg,
                background: palette.bg,
                border: `1px solid ${palette.line}`,
                padding: '2px 9px',
                borderRadius: 20,
                whiteSpace: 'nowrap',
            }}
        >
            {signal.type === 'gone_quiet' && signal.value == null ? (
                <FormattedMessage
                    id="agency.signal.noResponsesYet"
                    defaultMessage="No responses yet"
                />
            ) : (
                <FormattedMessage id={meta.id} defaultMessage={meta.def} values={{ value }} />
            )}
        </span>
    );
};

/** KPI stat tile for the portfolio band. */
export const StatTile: React.FunctionComponent<{
    label: React.ReactNode;
    value: React.ReactNode;
    detail?: React.ReactNode;
    accent?: string;
}> = ({ label, value, detail, accent }) => (
    <div
        style={{
            background: THEME.bgCard,
            border: `1px solid ${THEME.border}`,
            borderRadius: 16,
            boxShadow: THEME.cardShadow,
            padding: '18px 22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minWidth: 0,
        }}
    >
        <span
            style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: THEME.textMuted,
            }}
        >
            {label}
        </span>
        <span
            style={{
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: accent || THEME.textBase,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value}
        </span>
        {detail && (
            <span style={{ fontSize: 12.5, color: THEME.textSecondary }}>{detail}</span>
        )}
    </div>
);

/** Quartile → human benchmark position line. */
export const benchmarkPositionLabel = (
    quartile: 'top' | 'upper' | 'lower' | 'bottom' | undefined,
): { id: string; def: string } | null => {
    switch (quartile) {
        case 'top':
            return { id: 'agency.benchmark.topQuartile', def: 'Top quartile' };
        case 'upper':
            return { id: 'agency.benchmark.aboveMedian', def: 'Above median' };
        case 'lower':
            return { id: 'agency.benchmark.belowMedian', def: 'Below median' };
        case 'bottom':
            return { id: 'agency.benchmark.bottomQuartile', def: 'Bottom quartile' };
        default:
            return null;
    }
};
